// Toasts for "sent to the engine" and, when it lands, "engine result". A refusal is shown verbatim and stays until dismissed.
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { StatusPill } from "./StatusPill";

export interface ToastData { id: string; title: string; body?: string; state: string; sticky?: boolean }
interface ToastApi { push: (t: Omit<ToastData, "id"> & { id?: string }) => string; update: (id: string, t: Partial<ToastData>) => void; dismiss: (id: string) => void }

const Ctx = createContext<ToastApi | null>(null);
export const useToast = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useToast outside <ToastProvider>");
  return c;
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastData[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const dismiss = useCallback((id: string) => { setItems((xs) => xs.filter((x) => x.id !== id)); clearTimeout(timers.current.get(id)); }, []);
  const arm = useCallback((t: ToastData) => {
    clearTimeout(timers.current.get(t.id));
    if (!t.sticky && t.state !== "refused" && t.state !== "failed") timers.current.set(t.id, setTimeout(() => dismiss(t.id), 8000));
  }, [dismiss]);
  const api = useMemo<ToastApi>(() => ({
    push: (t) => {
      const id = t.id ?? Math.random().toString(36).slice(2);
      const full = { ...t, id };
      setItems((xs) => [...xs.filter((x) => x.id !== id), full].slice(-4));
      arm(full);
      return id;
    },
    update: (id, t) => setItems((xs) => xs.map((x) => { if (x.id !== id) return x; const n = { ...x, ...t }; arm(n); return n; })),
    dismiss,
  }), [arm, dismiss]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div role="status" aria-live="polite" aria-label="Notifications from the engine" className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[min(380px,calc(100vw-32px))] flex-col gap-2">
        {items.map((t) => (
          <div key={t.id} className="pointer-events-auto rounded-lg border border-line bg-raised p-3 shadow-pop">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <StatusPill state={t.state} />
                <p className="mt-1 text-sm font-medium">{t.title}</p>
                {t.body && <p className="mt-0.5 break-words text-xs text-muted">{t.body}</p>}
              </div>
              <button type="button" aria-label="Dismiss" onClick={() => dismiss(t.id)} className="rounded px-1 text-muted hover:bg-sunken">✕</button>
            </div>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
